import axios from 'axios';
import qs from 'qs';
import { get, isEmpty, isNil } from 'lodash';
import { useMutation, useQuery } from 'react-query';
import appUtils from 'src/components/appUtils';
import { toast } from 'src/components/index';
import questionsUtils from '../common/questionsUtils';
import api from '../services/api';

export const QUERY_KEYS = {
  COMPANY: 'company',
  COMPANY_QUESTIONS: 'companyQuestions',
  GROUP_MEMBERS: 'groupMembers',
  POPULATED_GROUPS: 'populatedGroups',
  PUBLIC_COMPANY: 'publicCompany'
};

export const useCompanyAsDataset = (
  companyid,
  viewerId = null,
  options = {}
) => {
  const { data, ...rest } = useQuery(
    [
      QUERY_KEYS.PUBLIC_COMPANY,
      companyid,
      ...(!isEmpty(viewerId) ? [viewerId] : [])
    ],
    () => api
      .get(
        `/company/public/${companyid}${viewerId ? `?viewerId=${viewerId}` : ''}`
      )
      .then((resp) => {
        const {
          success, error, message, warning
        } = resp;
        if (!success) {
          if (warning) {
            toast.show(message);
            return { success: false };
          }
          console.error(
            `GET /company/public/${companyid}${viewerId ? `?viewerId=${viewerId}` : ''}`,
            error
          );
          return toast.error(
            'Uh oh, we ran into an issue. Please try again later!'
          );
        }

        return resp;
      }),
    {
      enabled: Boolean(companyid),
      ...options
    }
  );

  return {
    data: get(data, 'data', {}),
    success: get(data, 'success'),
    ...rest
  };
};

export const useCompany = (queryOptions = {}) => useQuery(
  [
    QUERY_KEYS.COMPANY,
    ...(!isEmpty(queryOptions) ? [JSON.stringify(queryOptions)] : [])
  ],
  () => api
    .get('/company/company', {})
    .then((resp) => {
      if (!resp || !resp.success) {
        return;
      }
      const { company } = resp;
      if (!company) {
        return;
      }
      const companyQuestions = questionsUtils.questionsDbToApp(
        JSON.parse(company.questions)
      );
      company.questions = companyQuestions;

      const localDefaultDateFilter = appUtils.getDefaultDateFilter();
      const companyDefaultDateFilter = get(
        company,
        'settings.defaultDateFilter',
        false
      );

      if (
        companyDefaultDateFilter !== localDefaultDateFilter
            || (companyDefaultDateFilter && !localDefaultDateFilter)
      ) {
        appUtils.setDefaultDateFilter(companyDefaultDateFilter);
      }
      return company;
    })
    .catch((error) => {
      if (axios.isCancel(error)) {
        return console.error('[TEMP] useCompany request cancelled', error);
      }
      console.error('CATCH useCompany', error);
      throw error;
    }),
  {
    placeholderData: {
      settings: {
        contentEvents: false
      }
    },
    ...queryOptions,
    enabled: isNil(queryOptions.isEnabled)
      ? appUtils.isLoggedIn()
      : Boolean(queryOptions.isEnabled)
  }
);

export const uploadOrgQuery = () => useMutation((formData) => api.postForm('/company/upload', formData));

export const useQuestions = () => useQuery([QUERY_KEYS.COMPANY_QUESTIONS], () => api.get('/company/questions', {}).then((resp) => {
  if (!resp.success) {
    console.error('GET /admin/questions error');
    return;
  }
  const companyQuestions = questionsUtils.questionsDbToApp(
    JSON.parse(resp.questions)
  );
  return companyQuestions;
}));

export const resetCompanyQuery = () => useMutation(() => api.post('/company/reset'));

export const updateCompanyQuery = () => useMutation((data) => api.patch('/company/settings', { data }));

export const createGroupQuery = () => useMutation(({ name }) => api.post('/company/groups', { name }));

// update existing group's name (group id is required)
export const deleteGroupQuery = () => useMutation(({ id }) => api.delete('/company/groups', { id }));

export const useGroupMembers = (groupId) => useQuery(
  [QUERY_KEYS.GROUP_MEMBERS, groupId],
  () => {
    const stringified = qs.stringify({ groupId }, { skipNulls: true });
    return api
      .get(`/company/groupMembers?${stringified}`)
      .then((resp) => {
        if (!resp?.success) {
          console.error('GET /company/groupMembers error', resp);
          return null;
        }
        return resp;
      })
      .catch((error) => {
        console.error('useGroupMembers', error);
        throw error;
      });
  },
  {
    enabled: Boolean(groupId)
  }
);

export const usePopulatedGroups = () => useQuery([QUERY_KEYS.POPULATED_GROUPS], () => api.get(`/company/populatedGroups`).then((resp) => {
  if (!resp.success) {
    console.error('GET /company/populatedGroups error', resp);
    return null;
  }
  return resp;
}));

export const uploadImage = () => useMutation((formData) => api.postForm(`/company/uploadImage`, formData));
